'use strict';

import React, { useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import bb from 'billboard.js';
const d3 = require("d3");
const _  = {
    forEach: require('lodash/forEach'),
    map: require('lodash/map'),
}

import AccessibleTooltip from '../common/AccessibleTooltip';
import {H, SmartSection } from "../common/SmartHeadings";

const StringUtil = require('../../../../app/shared/StringUtil');
const Strings = require('../../../../app/shared/Strings');
const Globals = require('../../../../app/globals');

const DEFAULT_HEIGHT = 230;
const DEFAULT_WIDTH = 230;
const DEFAULT_TITLE_FS = 3;

const propTypes = {
    id: PropTypes.string.isRequired,
    className: PropTypes.string,
    chartClassName: PropTypes.string,
    columns: PropTypes.array,
    colors: PropTypes.object,
    title: PropTypes.string,
    summaryStat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.number,
    width: PropTypes.number,
    format: PropTypes.oneOf(['$', ',', '%']),
    displayTitleForNoData: PropTypes.bool,
    transitionDuration: PropTypes.number,
    tooltip: PropTypes.string,
    tooltipTitle: PropTypes.string,
    labelShow: PropTypes.bool,
    legendShow: PropTypes.bool,
    dataOrder: PropTypes.oneOf(['asc', 'desc']),
    labelColors: PropTypes.object,
    labelRatio: PropTypes.number,
    tableCaption: PropTypes.string,
    summaryRowName: PropTypes.string,
};

const defaultProps = {
    className: "",
    chartClassName: "",
    columns: [],
    colors: {},
    title: "",
    height: DEFAULT_HEIGHT,
    width: DEFAULT_WIDTH,
    format: ',',
    displayTitleForNoData: false,
    transitionDuration: 0,
    tooltip: "",
    tooltipTitle: "",
    labelShow: true,
    legendShow: false,
    dataOrder: null, // retains array ordering
    labelColors: null,
    labelRatio: null,
    tableCaption: '',
    summaryRowName: '',
}

function PopupDataTable(props) {
    let { id, ariaLabel, caption, columns, summaryRowName, summaryValue, format } = props.data;

    const elementRef = useRef(null);
    const [hasScrollbar, setHasScrollbar] = useState(false);

    const checkIfScrollable =()=>{
        const element = elementRef.current;
        if (element) {
            // For vertical scrollbar: check if content height > visible height
            const isScrollable = element.scrollHeight > element.clientHeight;
            setHasScrollbar(isScrollable);
        }
    };

    useLayoutEffect(()=>{
        checkIfScrollable(); // Initial check

        // Use ResizeObserver to detect changes in element's size or content size
        const resizeObserver = new ResizeObserver(() => {
            checkIfScrollable();
        });

        if (elementRef.current) {
            resizeObserver.observe(elementRef.current);
        }

        // Add a window resize listener as well to catch viewport changes
        window.addEventListener('resize', checkIfScrollable);

        return () => {
            // Cleanup observers and event listeners
            resizeObserver.disconnect();
            window.removeEventListener('resize', checkIfScrollable);
        };
    }, []); // Empty dependency array means this runs once on mount and cleans up on unmount

    return (
        <table ref={elementRef} id={id} className="popup-data-table" aria-label={ariaLabel} tabIndex={hasScrollbar ? 0 : undefined}>
            { caption &&
                <caption>{caption}</caption>
            }
            <tbody>
            {_.map(columns,(column,ci)=>{
                let name = column[0];
                let val = column[1];
                let f = d3.format(format);
                if (typeof f === 'function') {
                    val = f(val);
                }
                return (
                    <tr key={ci}>
                        <th scope="row">{name}</th>
                        <td>{val}</td>
                    </tr>
                )
            })}
            {(!!summaryRowName) &&
                <tr>
                    <th scope="row">{summaryRowName}</th>
                    <td>{summaryValue}</td>
                </tr>
            }
            </tbody>
        </table>

    );
}


class DonutChart extends React.Component {
    constructor(props) {
        super(props);
        
        this.chart = null;
        let noData = this.isChartEmpty(props);
        this.state = {
            noData,
            showTable: false,
        };

        this.formatSummaryStat = this.formatSummaryStat.bind(this);
        this._resizeSummaryStat = this._resizeSummaryStat.bind(this);
    }

    componentDidMount() {
        if (!this.state.noData) {
            this.generateChart(this.props);
        }
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        const noData = this.isChartEmpty(newProps);
        this.setState({noData},()=>{
            if (noData) {
                if (this.chart) {
                    this.chart.destroy(); // remove old element
                    delete this.chart;
                }
            }
            else {
                if (!this.chart) {
                    this.generateChart(newProps);
                }
                this.chart.load({
                    columns: newProps.columns,
                });
                this.chart.$.main.select(".bb-chart-arcs-title").text(this.formatSummaryStat(newProps.summaryStat));
            }
        });
    }

    componentWillUnmount() {
        if (this.chart) {
            delete this.chart;
        }
    }


    isChartEmpty=(props)=>{
        if (!props.columns || !props.columns.length) {
            return true;
        }
        var anyData = false;
        for (var i=0; i<props.columns.length; ++i) {
            let c = props.columns[i];
            for (var j=0; j<c.length; ++j) {
                let v = c[j];
                if (v && (typeof v === 'number')) {
                    anyData = true;
                    break;
                }
                else if ((j>0) && v && !isNaN(v)) {
                    anyData = true; // some score results are string type but actually numbers
                    break;
                }
            }
            if (anyData) {
                break;
            }
        }
        if (!anyData) {
            return true;
        }
        return false;
    }
    
    generateChart=(props)=>{
        //console.log('calling generateChart');
        const self = this;

        let colors = {...props.colors};
        _.forEach(props.columns,(entry,i)=>{
            let name = entry[0];
            if (typeof name === 'string') {
                if (!colors[name]) {
                    colors[name] = Globals.CHART_COLORS[i];
                } 
            }
        })

        let chartData = {
            bindto: '#'+props.id,
            data: {
                columns: props.columns,
                colors,
                order: props.dataOrder,
                type : 'donut'
            },
            donut: {
                expand: true,
                title: self.formatSummaryStat(props.summaryStat),
                label: {
                    show: props.labelShow
                }
            },
            legend: {
                show: props.legendShow
            },
            size: {
                height: props.height,
                width: props.width
            },
            tooltip: {
                format: {
                    value: function (value, ratio, id) {
                        var format = d3.format(props.format);
                        return format(value);
                    }
                },
                // add role="presentation" (template string taken and modified from billboard.js source code) - redmine #6948 / HEAZ-3625
                contents: {
                    template: `<table class="{=CLASS_TOOLTIP}" role="presentation"><tbody>
				{=TITLE}
				{{<tr class="{=CLASS_TOOLTIP_NAME}">
					<td class="name">${this.patterns ? `{=COLOR}` : `<span style="background-color:{=COLOR}"></span>`}{=NAME}</td>
					<td class="value">{=VALUE}</td>
				</tr>}}
			</tbody></table>`
                    
                }
            },
            transition: {
                duration: props.transitionDuration
            }
        }

        let labelObj = null;
        if (this.props.labelColors) {
            if (!labelObj) labelObj = {};

            labelObj.colors = this.props.labelColors;
        }

        if (!!labelObj) {
            chartData.data.labels = labelObj;
        }

        // label ratio is prop of chartData.donut.label rather than chartData.label
        if (typeof this.props.labelRatio === 'number') {
            chartData.donut.label.ratio = this.props.labelRatio;
        }

        this.chart = bb.generate(chartData);

        this._resizeSummaryStat();
    }

    formatSummaryStat(summaryStat) {
        if (typeof summaryStat === 'number') {
            return StringUtil.numberToLocaleString(summaryStat);
        }

        return summaryStat;
    }

    _resizeSummaryStat(newProps) {
        let props = newProps || this.props;

        if (typeof props.summaryStat !== 'undefined' && props.summaryStat !== null) {
            const useEm = (props.width / DEFAULT_WIDTH) * DEFAULT_TITLE_FS;
            this.chart.$.main.select(".bb-chart-arcs-title").style('font-size', useEm + "em");
        }
    }

    toggleDataTable=()=>{
        this.setState({showTable: !this.state.showTable});
    }

    render() {
        const showTableButton = !this.state.noData;
        let tooltipElem = null;
        if (this.props.tooltip || this.props.tooltipTitle) {
            tooltipElem = (
                <AccessibleTooltip
                    id={`${this.props.id}-tooltip`}
                    className={!showTableButton ? "margin-left-sm": ''}
                    title={this.props.tooltipTitle}
                    aria-label={"help - " + this.props.title}
                >
                    {this.props.tooltip}
                </AccessibleTooltip>
            );
        }

        let style;
        if (this.state.noData) {
            style = {
                width: this.props.width,
                height: this.props.height,
                borderRadius: this.props.width,
                border: "solid thin grey",
                position: "relative",
                marginTop: '1rem',
            }
        }

        //const noHeading = (!this.props.displayTitleForNoData && !this.props.columns.length);
        const tableElemId = `${this.props.id}-table`;
        const tableButtonTitle = this.state.showTable ? Strings['en'].common.HIDE_DATA : Strings['en'].common.SHOW_DATA;
        const tableButtonAriaLabel = this.state.showTable ?
            `${Strings['en'].analytics.assn_stats_charts.HIDE_DATA_FOR_CATEGORY}: ${this.props.title}`
            : `${Strings['en'].analytics.assn_stats_charts.SHOW_DATA_FOR_CATEGORY}: ${this.props.title}`;
        //const summaryName = 'Summary';
        const summaryValue = this.formatSummaryStat(this.props.summaryStat);
        
        return (
            <div className={`chart-container ${this.props.className}`}>
            {/*<div className={!!this.props.className ? `chart-container ${this.props.className}` : "chart-container"}>*/}
                <SmartSection component={
                 (!this.props.displayTitleForNoData && !this.props.columns.length) ?
                    null
                    : 
                    <div className="chart-heading-wrapper">
                        <H>
                            {this.props.title}
                        </H>
                        {(showTableButton) &&
                            <button
                                className="bare-text-button glyph show-data-button"
                                onClick={this.toggleDataTable}
                                title={tableButtonTitle}
                                aria-label={tableButtonAriaLabel}
                                aria-controls={tableElemId}
                                aria-describedby={tableElemId} // rec from HEAZ-7315.
                                // ...not sure if this is the intended use of aria-describedby though.
                                // ...shouldn't the _table_ be described by some text rather than the button being described by the table?
                            >
                                <i className="fa fa-table" aria-hidden="true" />
                                <span className="sr-only">{tableButtonTitle}</span>
                            </button>
                        }
                        {tooltipElem}
                    </div>
                }>
                <div className="chart-wrapper">
                    {/*span className="center-stat centered-vh">{this.props.summaryStat}</span>*/}
                    <div className={this.props.chartClassName} aria-hidden="true" id={this.props.id} />
                    {(!!this.state.noData) &&
                        <div className="no-data-overlay" style={style} role="presentation">
                            <span className="centered-vh">{Strings['en'].common.NO_DATA}</span> 
                        </div>
                    }
                    {(!!this.state.showTable) && 
                        <PopupDataTable data={{
                            id:tableElemId,
                            ariaLabel: this.props.title,
                            caption: this.props.tableCaption,
                            columns: this.props.columns,
                            summaryRowName: this.props.summaryRowName,
                            summaryValue,
                            format: this.props.format,
                        }} />
                    }
                </div>
                </SmartSection>
            </div>
        );
    }
}

DonutChart.propTypes = propTypes;
DonutChart.defaultProps = defaultProps;

export default DonutChart;